import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'

import { handlerServerError } from '@orbit/errors'

import { useHRRContext } from '../../context'
import { HRR_DETAILS_KEY } from '../../query'

import * as SC from './styles'

export const Footer = () => {
  const { data } = useHRRContext()
  const { enableRecalculate, highRiskReviewId, enableAcknowledgeAllWarnings, enableReview } = data
  const confirmation = useConfirmation()
  const queryClient = useQueryClient()

  const handleRecalculate = async () => {
    await confirmation.showAskConfirmation({
      message: `Are you sure you want to recalculate the report? It may change report status.`,
      submitButtonText: 'Recalculate',
      confirmCallback: async () => recalculate({ id: highRiskReviewId })
    })
  }

  const { mutate: recalculate } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.hrrRecalculate, opts),
    onSuccess: () => {
      Toast.successSmall('Report has been saved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const handleAcknowledge = async () => {
    await confirmation.showAskConfirmation({
      message: `Are you sure you want to acknowledge all warnings?`,
      submitButtonText: 'Confirm',
      confirmCallback: async () => acknowledge({ id: highRiskReviewId })
    })
  }

  const { mutate: acknowledge } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.hrrAcknowledge, opts),
    onSuccess: () => {
      Toast.successSmall('All warnings have been resolved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const handleReview = async () => {
    await confirmation.showAskConfirmation({
      message: `Are you sure you want to review this report?`,
      submitButtonText: 'Review',
      confirmCallback: async () => review({ id: highRiskReviewId })
    })
  }

  const { mutate: review } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.hrrReview, opts),
    onSuccess: () => {
      Toast.successSmall('Report has been saved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  return (
    <SC.Box>
      {enableRecalculate && (
        <Button onClick={handleRecalculate} face="tertiary">
          Recalculate
        </Button>
      )}

      <ButtonGroup margin="small">
        {enableAcknowledgeAllWarnings && (
          <Button onClick={handleAcknowledge} face="tertiary">
            Acknowledge all Warnings
          </Button>
        )}
        {enableReview && (
          <Button onClick={handleReview} face="primary">
            Review
          </Button>
        )}
      </ButtonGroup>
    </SC.Box>
  )
}
